// One hook for the settings singleton, shared by every page in this resource.
//
// Modelled on resources/jaguar_owned_controls/useJaguarOwnedSwitches.ts, with
// one deliberate simplification: mode, channels, caps and quiet hours all live
// on a SINGLE ROW, so a change is one PATCH. The Jaguar-Owned page needed
// ninety lines of comment to make a multi-row write safe against partial
// application; this feature avoids that class of bug by construction.

import { useCallback, useEffect, useState } from 'react'
import { supabaseClient } from '../../lib/supabaseClient'
import type { CmSettings } from './types'

export interface CmSettingsState {
  settings: CmSettings | null
  isLoading: boolean
  isError: boolean
  /** The master kill switch, read from feature_flags rather than this row. */
  masterEnabled: boolean | null
  refetch: () => Promise<void>
  save: (patch: Partial<CmSettings>) => Promise<void>
  setMaster: (enabled: boolean) => Promise<void>
}

export const MASTER_FLAG_KEY = 'community_manager_enabled'

export function useCmSettings(): CmSettingsState {
  const [settings, setSettings] = useState<CmSettings | null>(null)
  const [masterEnabled, setMasterEnabled] = useState<boolean | null>(null)
  const [isLoading, setLoading] = useState(true)
  const [isError, setError] = useState(false)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(false)
    const [{ data: s, error: se }, { data: f, error: fe }] = await Promise.all([
      supabaseClient.from('cm_settings').select('*').eq('id', true).maybeSingle(),
      supabaseClient.from('feature_flags').select('enabled').eq('key', MASTER_FLAG_KEY).maybeSingle(),
    ])
    if (se || fe) setError(true)
    setSettings((s as CmSettings | null) ?? null)
    // null (not false) when unreadable: the Controls page must be able to tell
    // "switched off" apart from "we could not find out".
    setMasterEnabled(fe ? null : (f?.enabled ?? false))
    setLoading(false)
  }, [])

  useEffect(() => { void refetch() }, [refetch])

  const save = useCallback(async (patch: Partial<CmSettings>) => {
    const { error } = await supabaseClient.from('cm_settings').update(patch).eq('id', true)
    if (error) throw new Error(error.message)
    await refetch()
  }, [refetch])

  const setMaster = useCallback(async (enabled: boolean) => {
    const { error } = await supabaseClient
      .from('feature_flags').update({ enabled }).eq('key', MASTER_FLAG_KEY)
    if (error) throw new Error(error.message)
    await refetch()
  }, [refetch])

  return { settings, masterEnabled, isLoading, isError, refetch, save, setMaster }
}
